import { type FC, type PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { type Cache, SWRConfig, type SWRConfiguration, type SWRResponse, useSWRConfig } from 'swr';

import { AuthContext } from '@/providers/Auth';
import { type AuthUser } from '@/providers/Auth/constants';
import { logError } from '@/providers/ErrorTracking';

const CACHE_KEY = 'glade-cache';
const CACHE_USER_KEY = 'glade-cache-user';
const WRITE_INTERVAL = 8_000;
const IDLE_TIMEOUT = 6_000;
const EMPTY_CACHE = '[]';
const MAX_CACHE_ENTRIES = 5_000;
// Cap on shrink-and-retry attempts before we give up and clear the cache.
// 8 halvings of 5,000 entries shrinks down to ~20, well below anything that
// could overflow even a 5MB quota with worst-case per-entry payloads.
const MAX_QUOTA_RETRY_ATTEMPTS = 8;

// `localStorage` quota errors throw a `DOMException` whose representation
// varies by browser. Chrome/Edge use `name: 'QuotaExceededError'` plus
// `code: 22`, Firefox emits `name: 'NS_ERROR_DOM_QUOTA_REACHED'` with
// `code: 1014`, and older WebKit only sets the legacy code. Match all three
// shapes so the recovery path fires on every browser we ship.
const isQuotaExceededError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const errorObject = error as { name?: string; code?: number };
  return (
    errorObject.name === 'QuotaExceededError' ||
    errorObject.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    errorObject.code === 22 ||
    errorObject.code === 1014
  );
};

// Module-level guards: surface a single Sentry breadcrumb per page load for
// each distinct outcome (shrunk, cleared, unexpected non-quota error). Without
// these, the existing 1.1K Sentry events / week from a handful of users
// (DEV-23536 / NOODLE-FRONTEND-FFP) would have moved into a quieter level but
// still fired on every interval tick once a session crosses the quota.
//
// The three flags are intentionally independent: an unexpected SecurityError
// shouldn't be hidden just because we previously reported a shrink, and vice
// versa.
let hasReportedQuotaShrink = false;
let hasReportedQuotaClear = false;
let hasReportedUnexpectedStorageError = false;

// Exported only so the spec can reset between tests; do not call from app code.
export const __resetSwrCacheQuotaReportingForTests = (): void => {
  hasReportedQuotaShrink = false;
  hasReportedQuotaClear = false;
  hasReportedUnexpectedStorageError = false;
};

type WriteCacheOutcome = 'ok' | 'shrunk' | 'cleared' | 'failed';

export const writeCacheWithQuotaFallback = (
  storage: Storage,
  entries: Array<[string, unknown]>,
  userId: string | undefined,
): WriteCacheOutcome => {
  // Try writing the full payload first. On quota error, drop the oldest
  // half of the entries (Map iteration order is insertion order, and newer
  // SWR queries are likelier to be re-used on next load) and retry. If
  // we run out of budget, clear the cache key entirely so a poisoned blob
  // can't sit in storage forever.
  let toWrite = entries;
  let droppedAny = false;

  for (let attempt = 0; attempt <= MAX_QUOTA_RETRY_ATTEMPTS; attempt += 1) {
    try {
      storage.setItem(CACHE_KEY, JSON.stringify(toWrite));
      try {
        storage.setItem(CACHE_USER_KEY, JSON.stringify(userId));
      } catch {
        // user-id is tiny (a string ID), so a quota error here is vanishingly
        // unlikely. If it does happen the entries we just wrote will be
        // discarded on the next load (the user-id mismatch check), which is
        // the safe default. Drop silently.
      }
      return droppedAny ? 'shrunk' : 'ok';
    } catch (error) {
      if (!isQuotaExceededError(error)) {
        // Anything other than a quota overflow (e.g., security errors,
        // disabled storage) is unrecoverable and unrelated to this fix.
        // Surface it once so we don't silently mask new failure modes.
        if (!hasReportedUnexpectedStorageError) {
          hasReportedUnexpectedStorageError = true;
          logError(error, {
            handledBy: 'SwrCache.writeCacheWithQuotaFallback',
            level: 'warning',
            phase: 'unexpected-storage-error',
          });
        }
        return 'failed';
      }
      droppedAny = true;
      if (toWrite.length === 0) break;
      // Drop the oldest half of the entries; round up so we eventually hit 0.
      toWrite = toWrite.slice(Math.ceil(toWrite.length / 2));
    }
  }

  // We exhausted retries while still blowing past quota. Clear the cache key
  // so the next session starts fresh instead of trying to read a stale,
  // possibly-truncated blob, then surface a single Sentry breadcrumb so we
  // can monitor how often this happens in production.
  try {
    storage.removeItem(CACHE_KEY);
  } catch {
    // best-effort
  }
  if (!hasReportedQuotaClear) {
    hasReportedQuotaClear = true;
    logError(new Error('SwrCache: localStorage quota exceeded; cache cleared'), {
      handledBy: 'SwrCache.writeCacheWithQuotaFallback',
      level: 'warning',
      maxRetryAttempts: MAX_QUOTA_RETRY_ATTEMPTS,
      originalEntryCount: entries.length,
    });
  }
  return 'cleared';
};

const createLocalStorageProvider = (user: AuthUser | null): (() => Cache) => {
  const prevUserStr = window.localStorage.getItem(CACHE_USER_KEY);
  let initialStore: [string, SWRResponse][] = [];
  try {
    // this intentionally fails to retrieve when user is `null`.
    const raw =
      prevUserStr && user && user?.id === JSON.parse(prevUserStr)
        ? (window.localStorage.getItem(CACHE_KEY) ?? EMPTY_CACHE)
        : EMPTY_CACHE;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((entry) => Array.isArray(entry) && entry.length === 2)) {
      initialStore = (parsed as [string, SWRResponse][])
        .slice(0, MAX_CACHE_ENTRIES)
        .filter(([_, res]) => !res.isLoading && !res.isValidating);
    }
  } catch {
    initialStore = [];
  }

  // When initializing, we restore the data from `localStorage` into a map.
  const map = new Map(initialStore) as unknown as Cache;

  return () => map;
};

const SwrCacheImpl: FC<PropsWithChildren<{ userId?: AuthUser['id'] }>> = ({ userId, children }) => {
  const { cache, mutate } = useSWRConfig();
  const prevUserIdRef = useRef<AuthUser['id'] | undefined>(undefined);
  useEffect(() => {
    // We write the cache as a JSON array of entries.
    //
    // localStorage has a per-origin quota (~5MB in most browsers). For
    // long-lived sessions that accumulate chat history, document lists, and
    // questionnaire responses, the full cache can exceed this and throw
    // QuotaExceededError on every interval tick AND on `beforeunload`
    // (DEV-23536 / NOODLE-FRONTEND-FFP). Delegate to the helper so we
    // shrink-and-retry instead of letting the error bubble to Sentry.
    const writeToCache = (): void => {
      const entries = Array.from((cache as unknown as Map<string, unknown>).entries());
      const outcome = writeCacheWithQuotaFallback(window.localStorage, entries, userId);
      if (outcome === 'shrunk' && !hasReportedQuotaShrink) {
        hasReportedQuotaShrink = true;
        logError(new Error('SwrCache: localStorage quota exceeded; cache shrunk'), {
          handledBy: 'SwrCache.writeToCache',
          level: 'info',
          originalEntryCount: entries.length,
        });
      }
    };
    // We also write to `localStorage` on an interval.
    const intervalId = window.setInterval(() => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(writeToCache, { timeout: IDLE_TIMEOUT });
      } else {
        writeToCache();
      }
    }, WRITE_INTERVAL);
    // Before unloading the app, we write back all the data into `localStorage`
    // and clear the interval. Clean up the listener on unmount.
    const handleBeforeUnload = (): void => {
      writeToCache();
      clearInterval(intervalId);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [cache, userId]);

  // Clear SWR cache and localStorage only when the userId changes (not on initial mount)
  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    if (prevUserId === undefined) {
      // Initial mount: just record the current userId
      prevUserIdRef.current = userId;
      return;
    }
    if (prevUserId !== userId) {
      // User has changed: clear SWR cache and reset persisted cache
      mutate(() => true, undefined, { revalidate: false });
      window.localStorage.setItem(CACHE_KEY, EMPTY_CACHE);
      window.localStorage.setItem(CACHE_USER_KEY, JSON.stringify(userId));
      prevUserIdRef.current = userId;
    }
  }, [userId, mutate]);
  return children;
};

export const SwrCache: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [configValue, setConfigValue] = useState<SWRConfiguration | null>(null);
  // This has to be in a `useEffect` to avoid NextJS trying to run it on the server.
  useEffect(() => {
    const provider = createLocalStorageProvider(user);
    setConfigValue({ provider } satisfies SWRConfiguration);
  }, [user]);
  return configValue ? (
    <SWRConfig value={configValue}>
      <SwrCacheImpl userId={user?.id ?? undefined}>{children}</SwrCacheImpl>
    </SWRConfig>
  ) : (
    children
  );
};
